import {
  addWildcardToRoutes,
  FundingSourceType,
  RouteElement,
  useGuestPayorPaymentRequestDetails,
  useNavigate,
} from '@melio/ar-domain';
import { forwardRef } from '@melio/platform-utils';
import { useCallback, useEffect, useState } from 'react';
import { Route, Routes } from 'react-router-dom';

import {
  AddBankFundingSourceActivity,
  CustomPaymentActivity,
  PayByBankActivity,
  V2AddCardFundingSourceActivity,
} from '../activities';
import { NewGuestPaymentLayout, PaymentLayout } from '../layout';

export type GuestPaymentRouterFlowProps = {
  paymentRequestLink: string;
  onViewInvoice: VoidFunction;
};
export const GuestPaymentRouterFlow = forwardRef<GuestPaymentRouterFlowProps>(
  ({ paymentRequestLink, onViewInvoice }, ref) => {
    const { data, isLoading } = useGuestPayorPaymentRequestDetails({ paymentRequestLink });
    const { Paths, goToBank, goToCard, goToCustom, goToPayByBank } = useGuestPaymentRouterFlow();

    const [selectedFundingSource, setSelectedFundingSource] = useState<FundingSourceType>();

    const handleFundingSourceSelection = useCallback((type: FundingSourceType) => {
      setSelectedFundingSource(type);
      type == 'bank-account' ? goToBank() : goToCard();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      if (!data) return;

      if (data.invoice.paymentOptions.isCardAllowed) handleFundingSourceSelection('card');
      else if (data.invoice.paymentOptions.isAchAllowed) handleFundingSourceSelection('bank-account');
      else goToCustom();
    }, [data, handleFundingSourceSelection]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!data) {
      return <NewGuestPaymentLayout isLoading ref={ref} />;
    }

    return (
      <Routes>
        <Route
          element={
            <PaymentLayout
              onViewInvoice={onViewInvoice}
              payeeDetails={data}
              isLoading={isLoading}
              onSelectFundingSource={handleFundingSourceSelection}
              selectedFundingSource={selectedFundingSource}
              ref={ref}
            />
          }
        >
          <Route
            path={Paths.Custom}
            element={<CustomPaymentActivity paymentRequestLink={paymentRequestLink} onViewInvoice={onViewInvoice} />}
          />
          <Route
            path={Paths.Bank}
            element={<AddBankFundingSourceActivity paymentRequestLink={paymentRequestLink} onDone={goToPayByBank} />}
          />
          <Route
            path={Paths.PayByBank}
            element={
              <RouteElement
                component={PayByBankActivity}
                pathToProps={{ fundingSourceId: 'fundingSourceId' }}
                onDone={() => null}
                paymentRequestLink={paymentRequestLink}
              />
            }
          />
          <Route
            path={Paths.Card}
            element={<V2AddCardFundingSourceActivity paymentRequestLink={paymentRequestLink} onDone={() => null} />}
          />
        </Route>
      </Routes>
    );
  }
);

const useGuestPaymentRouterFlow = () => {
  enum Paths {
    Custom = 'custom',
    Bank = 'bank',
    PayByBank = 'bank/:fundingSourceId',
    Card = 'card',
  }
  const navigate = useNavigate<Paths>({ withSearchparams: true });

  const goToBank = () => navigate(Paths.Bank);
  const goToPayByBank = (fundingSourceId: string) => navigate(Paths.PayByBank, { pathParams: { fundingSourceId } });
  const goToCard = () => navigate(Paths.Card);
  const goToCustom = () => navigate(Paths.Custom);

  return {
    RedirectPaths: Paths,
    Paths: addWildcardToRoutes(Paths),
    goToBank,
    goToCard,
    goToCustom,
    goToPayByBank,
  };
};
